import PropTypes from 'prop-types';
import React from 'react';
import isEqual from 'react-fast-compare';
import {connect} from 'react-redux';
import _, {compact} from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {CollapsedTableReduxActions} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {
  createOutputTableCollapseId,
  shouldCollapseTableInitially,
} from 'notebook/tables/collapse/useTableCollapse';
import {BenchlingState} from 'redux-stores/reduceApp';
import CreateRequestFromSamplesButton, {
  SampleLookupInfoForCreateRequest,
} from 'requests/creation/CreateRequestFromSamplesButton';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import {TableCollapserContext} from 'ui-library/tables/TableCollapserContext';
import {ElementMouseEvent} from 'util/Types';
import StageRunSampleLink from 'workflows/components/StageRunSampleLink';
import OptionalContainerLinkWithLocation from 'workflows/stage-entries/components/OptionalContainerLinkWithLocation';
import {
  SAMPLE_COLUMN_LABEL,
  STAGE_RUN_FULL_SHAPE,
  STAGE_RUN_SAMPLE_FULL_SHAPE,
  StageRun,
  StageRunSample,
} from 'workflows/WorkflowConstants';

import getOutputTableRowId from './getOutputTableRowId';
import {getSelectedSampleIds} from './WorkflowTableUtil';

function mapStateToProps(state: BenchlingState, props: BaseOutputRichObjectTableProps): ConnectedProps {
  // Never collapse when printing.
  if (props.viewer) {
    return {isCollapsed: false};
  }
  return {
    isCollapsed: state.ui.collapsedTables[createOutputTableCollapseId(props.stageEntryId)] ?? true,
  };
}

interface BaseOutputRichObjectTableProps {
  locked: boolean;
  viewer: boolean;
  stageEntryId: string;
  headerCheckBoxSelected: boolean;
  selectedRowIds: {[rowId: string]: boolean};
  orderedOutputSamples: Array<{stageRunId: string; stageRunSampleId: string}>;
  stageRuns: Array<StageRun>;
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample};
  onOpenAddSampleModal: (e: ElementMouseEvent) => void;
  onRemoveOutputSamples: (e: ElementMouseEvent) => void;
  onToggleHeaderCheckbox: () => void;
  onToggleSelection: (rowId: string) => void;
}

interface ConnectedProps {
  isCollapsed: boolean;
}

type OutputRichObjectTableProps = BaseOutputRichObjectTableProps & ConnectedProps;
/**
 * Subcomponent that lets us avoid re-rendering the whole table on every update.
 *
 * Unlike the full OutputTable, this component doesn't depend on the entire entry (`OutputTable.props.note`),
 * so the props don't change when the user types in the entry.
 *
 * See https://benchling.engineering/performance-engineering-with-react-e03013e53285#745c
 * for a more detailed explanation
 */
class OutputRichObjectTable extends React.Component<OutputRichObjectTableProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    locked: PropTypes.bool.isRequired,
    viewer: PropTypes.bool.isRequired,
    stageEntryId: PropTypes.string.isRequired,
    headerCheckBoxSelected: PropTypes.bool.isRequired,
    selectedRowIds: PropTypes.objectOf(PropTypes.bool.isRequired).isRequired,

    orderedOutputSamples: PropTypes.arrayOf(
      PropTypes.shape({
        stageRunId: PropTypes.string.isRequired,
        stageRunSampleId: PropTypes.string.isRequired,
      })
    ),
    stageRuns: PropTypes.arrayOf(STAGE_RUN_FULL_SHAPE.isRequired).isRequired,
    stageRunSampleById: PropTypes.objectOf(STAGE_RUN_SAMPLE_FULL_SHAPE.isRequired).isRequired,

    onOpenAddSampleModal: PropTypes.func.isRequired,
    onRemoveOutputSamples: PropTypes.func.isRequired,
    onToggleHeaderCheckbox: PropTypes.func.isRequired,
    onToggleSelection: PropTypes.func.isRequired,

    // mapStateToProps
    isCollapsed: PropTypes.bool.isRequired,
  };

  shouldComponentUpdate(nextProps: OutputRichObjectTableProps): boolean {
    // Shallow comparison doesn't work because mapStateToProps creates new objects on every update.
    // See https://benchling.engineering/performance-engineering-with-react-e03013e53285#9f7c
    // for a more detailed explanation
    return !isEqual(this.props, nextProps);
  }

  componentDidMount(): void {
    const initialCollapsedState = this._isCollapsedInitially();
    CollapsedTableReduxActions.init(
      createOutputTableCollapseId(this.props.stageEntryId),
      initialCollapsedState
    );
  }

  _isCollapsedInitially = (): boolean => {
    const {rowCount, cellCount} = this._getDimensions();
    return shouldCollapseTableInitially(cellCount, rowCount, null);
  };

  _toggleTableCollapsed = (): void => {
    CollapsedTableReduxActions.toggle(createOutputTableCollapseId(this.props.stageEntryId));
  };

  _renderHeaderCheckbox(): JSX.Element | null {
    return !this.props.locked ? (
      <RichObjectTable.CheckboxCell
        checked={this.props.headerCheckBoxSelected}
        onChange={this.props.onToggleHeaderCheckbox}
      />
    ) : null;
  }

  _getHeaderCells(): Array<string | JSX.Element | null> {
    return [this._renderHeaderCheckbox(), SAMPLE_COLUMN_LABEL, 'Container', 'Stage Run'];
  }

  _renderCheckbox(rowId: string): JSX.Element {
    return (
      <RichObjectTable.CheckboxCell
        checked={Boolean(this.props.selectedRowIds[rowId])}
        onChange={() => {
          this.props.onToggleSelection(rowId);
        }}
        disabled={this.props.locked}
      />
    );
  }

  _getValueCells(): Array<JSX.Element> {
    const stageRunsById = _.indexBy(this.props.stageRuns, 'id');

    const rows = [];
    // Add a row for each stage run + sample pair from orderedInputSamples
    for (const {stageRunId, stageRunSampleId} of this.props.orderedOutputSamples) {
      const stageRun = stageRunsById[stageRunId];
      assert(stageRun != null, 'No stage run with the id was found', {stageRunId});
      const rowId = getOutputTableRowId(stageRun.id, stageRunSampleId);
      const sample = this.props.stageRunSampleById[stageRunSampleId];

      rows.push(
        <RichObjectTable.Row key={rowId}>
          <RichObjectTable.Cell primary={true}>{this._renderCheckbox(rowId)}</RichObjectTable.Cell>
          <RichObjectTable.Cell primary={true}>
            <StageRunSampleLink sample={sample} />
          </RichObjectTable.Cell>
          {sample ? (
            <RichObjectTable.Cell primary={true}>
              <OptionalContainerLinkWithLocation containerId={sample.containerId} />
            </RichObjectTable.Cell>
          ) : (
            <RichObjectTable.Cell>
              <span className="u-faded u-italic">Loading</span>
            </RichObjectTable.Cell>
          )}
          <RichObjectTable.Cell>{stageRun.name}</RichObjectTable.Cell>
        </RichObjectTable.Row>
      );
    }

    if (rows.length === 0) {
      rows.push(
        <RichObjectTable.Row key="empty-row">
          <RichObjectTable.HeaderCell />
          <RichObjectTable.Cell>
            <div
              className="u-faded"
              {...getTestProps({component: 'OutputRichObjectTable', element: 'noSamplesText'})}
            >
              No samples
            </div>
          </RichObjectTable.Cell>
          <RichObjectTable.Cell />
          <RichObjectTable.Cell />
        </RichObjectTable.Row>
      );
    }

    return rows;
  }

  _getDimensions(): {rowCount: number; cellCount: number} {
    const rowCount = this._getValueCells().length;
    const cellCount = rowCount * 4; // checkbox, sample, container, stage run
    return {
      rowCount,
      cellCount,
    };
  }

  _renderCreateRequestButton(): JSX.Element {
    const sampleInfos: Array<SampleLookupInfoForCreateRequest> = compact(
      getSelectedSampleIds(
        this.props.orderedOutputSamples,
        this.props.stageRunSampleById,
        this.props.selectedRowIds
      )
    ).map((sampleId) => ({
      idForLookup: sampleId,
    }));
    return (
      <CreateRequestFromSamplesButton
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        className="u-mini-spacing-right"
        sampleInfos={sampleInfos}
        size={ButtonSize.SMALL}
      />
    );
  }

  _renderRightHeader(): JSX.Element {
    return (
      <div className="u-flex-display">
        {!this.props.locked && BenchlingConfig.ENABLE_REQUESTS && this._renderCreateRequestButton()}
        {!this.props.locked && (
          <Button
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            size={ButtonSize.SMALL}
            onClick={this.props.onRemoveOutputSamples}
            text="Remove"
            disabled={!Object.values(this.props.selectedRowIds).some((isSelected) => isSelected)}
          />
        )}
      </div>
    );
  }

  render(): JSX.Element {
    const rows = this._getValueCells();
    return (
      // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
      <TableCollapserContext.Provider value={{isTableInitiallyCollapsed: this._isCollapsedInitially()}}>
        <RichObjectTable.Table
          className="noteEditor-richObjectTable"
          collapsedHeaderText={`${rows.length} ${pluralize(rows.length, 'row')}`}
          isCollapsed={this.props.isCollapsed}
          isCollapsible={true}
          leftHeader={
            <div className="u-vertical-center-items">
              <div>Output Samples</div>
              {!this.props.locked && (
                <RichObjectTable.AddButton
                  className="u-mini-spacing-left"
                  onClick={this.props.onOpenAddSampleModal}
                />
              )}
            </div>
          }
          rightHeader={this._renderRightHeader()}
          headerCells={this._getHeaderCells()}
          columns={[
            <RichObjectTable.CheckboxCol key="firstCol" />,
            ..._.range(3).map((i) => <col key={i} width={RichObjectTable.CONTENT_COLUMN_MIN_WIDTH} />),
          ]}
          onToggleCollapsed={this._toggleTableCollapsed}
          dataTestAttrs={{component: 'OutputRichObjectTable', element: 'root'}}
        >
          {rows}
        </RichObjectTable.Table>
      </TableCollapserContext.Provider>
    );
  }
}

export default connect(mapStateToProps)(OutputRichObjectTable);
